<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import SectionLayout from './SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const pixKey = ref('');
const pixKeyType = ref(null);
const merchantName = ref('');
const merchantCity = ref('');
const isSubmitting = ref(false);

const pixKeyTypeOptions = computed(() => [
  { id: 'cpf', name: t('GENERAL_SETTINGS.FORM.PIX.KEY_TYPE.OPTIONS.CPF') },
  { id: 'cnpj', name: t('GENERAL_SETTINGS.FORM.PIX.KEY_TYPE.OPTIONS.CNPJ') },
  { id: 'phone', name: t('GENERAL_SETTINGS.FORM.PIX.KEY_TYPE.OPTIONS.PHONE') },
  { id: 'email', name: t('GENERAL_SETTINGS.FORM.PIX.KEY_TYPE.OPTIONS.EMAIL') },
  {
    id: 'random',
    name: t('GENERAL_SETTINGS.FORM.PIX.KEY_TYPE.OPTIONS.RANDOM'),
  },
]);

watch(
  currentAccount,
  () => {
    const {
      pix_key: key,
      pix_key_type: keyType,
      pix_merchant_name: name,
      pix_merchant_city: city,
    } = currentAccount.value?.settings || {};

    pixKey.value = key || '';
    pixKeyType.value =
      pixKeyTypeOptions.value.find(option => option.id === keyType) || null;
    merchantName.value = name || '';
    merchantCity.value = city || '';
  },
  { deep: true, immediate: true }
);

const handleSubmit = async () => {
  try {
    isSubmitting.value = true;
    await updateAccount(
      {
        pix_key: pixKey.value,
        pix_key_type: pixKeyType.value?.id || '',
        pix_merchant_name: merchantName.value,
        pix_merchant_city: merchantCity.value,
      },
      { silent: true }
    );
    useAlert(t('GENERAL_SETTINGS.FORM.PIX.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.PIX.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.PIX.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.PIX.NOTE')"
    with-border
  >
    <form class="grid gap-5" @submit.prevent="handleSubmit">
      <WithLabel :label="t('GENERAL_SETTINGS.FORM.PIX.KEY_TYPE.LABEL')">
        <SingleSelect
          v-model="pixKeyType"
          :options="pixKeyTypeOptions"
          :placeholder="t('GENERAL_SETTINGS.FORM.PIX.KEY_TYPE.PLACEHOLDER')"
          placeholder-icon="i-lucide-key-round"
          variant="faded"
        />
      </WithLabel>
      <WithLabel
        :label="t('GENERAL_SETTINGS.FORM.PIX.KEY.LABEL')"
        :help-message="t('GENERAL_SETTINGS.FORM.PIX.KEY.HELP')"
      >
        <NextInput
          v-model="pixKey"
          type="text"
          class="w-full"
          :placeholder="t('GENERAL_SETTINGS.FORM.PIX.KEY.PLACEHOLDER')"
        />
      </WithLabel>
      <WithLabel :label="t('GENERAL_SETTINGS.FORM.PIX.MERCHANT_NAME.LABEL')">
        <NextInput
          v-model="merchantName"
          type="text"
          class="w-full"
          :placeholder="
            t('GENERAL_SETTINGS.FORM.PIX.MERCHANT_NAME.PLACEHOLDER')
          "
        />
      </WithLabel>
      <WithLabel :label="t('GENERAL_SETTINGS.FORM.PIX.MERCHANT_CITY.LABEL')">
        <NextInput
          v-model="merchantCity"
          type="text"
          class="w-full"
          :placeholder="
            t('GENERAL_SETTINGS.FORM.PIX.MERCHANT_CITY.PLACEHOLDER')
          "
        />
      </WithLabel>
      <div class="flex gap-2">
        <NextButton
          blue
          type="submit"
          :is-loading="isSubmitting"
          :label="t('GENERAL_SETTINGS.FORM.PIX.UPDATE_BUTTON')"
        />
      </div>
    </form>
  </SectionLayout>
</template>
